/**
 * ChartComponent - Wrapper para Chart.js
 */
import { useRef, useEffect, useMemo } from 'react'
import Chart from 'chart.js/auto'
import { useTheme } from '@context/ThemeContext'
import { CHART_THEME } from '@config/visualTheme'

export default function ChartComponent({ type, data, options, title }) {
  const canvasRef = useRef(null)
  const chartRef = useRef(null)
  const { isDarkMode } = useTheme()

  const themedOptions = useMemo(() => {
    const textColor = isDarkMode ? CHART_THEME.textDark : CHART_THEME.textLight
    const mutedColor = isDarkMode ? CHART_THEME.mutedDark : CHART_THEME.mutedLight
    const gridColor = isDarkMode ? CHART_THEME.gridDark : CHART_THEME.gridLight
    const customPlugins = options?.plugins || {}
    const customLegend = customPlugins.legend || {}
    const customLegendLabels = customLegend.labels || {}
    const customTitle = customPlugins.title || {}
    const customScales = options?.scales || {}

    const plugins = {
      title: {
        display: !!title,
        text: title,
        color: textColor,
        ...customTitle,
      },
      legend: {
        position: type === 'doughnut' ? 'bottom' : 'top',
        align: 'start',
        labels: {
          color: textColor,
          usePointStyle: true,
          boxWidth: 7,
          boxHeight: 7,
          padding: 18,
          font: { family: 'Geist Variable', size: 11, weight: 500 },
          ...customLegendLabels,
        },
        ...customLegend,
      },
      ...customPlugins,
    }

    // Reaplica o merge profundo dos labels após o spread de plugins/legend.
    plugins.legend = {
      position: type === 'doughnut' ? 'bottom' : 'top',
      align: 'start',
      ...customLegend,
      labels: {
        color: textColor,
        usePointStyle: true,
        boxWidth: 7,
        boxHeight: 7,
        padding: 18,
        font: { family: 'Geist Variable', size: 11, weight: 500 },
        ...customLegendLabels,
      },
    }
    plugins.title = {
      display: !!title,
      text: title,
      color: textColor,
      ...customTitle,
    }

    const scales = type === 'doughnut'
      ? customScales
      : {
          ...customScales,
          x: {
            border: { color: gridColor, ...(customScales.x?.border || {}) },
            grid: { color: gridColor, ...(customScales.x?.grid || {}) },
            ticks: { color: mutedColor, ...(customScales.x?.ticks || {}) },
            ...(customScales.x || {}),
          },
          y: {
            beginAtZero: true,
            border: { color: gridColor, ...(customScales.y?.border || {}) },
            grid: { color: gridColor, ...(customScales.y?.grid || {}) },
            ticks: { color: mutedColor, ...(customScales.y?.ticks || {}) },
            ...(customScales.y || {}),
          },
        }

    // Garante que objetos internos não sejam perdidos pelo spread dos eixos customizados.
    if (type !== 'doughnut') {
      scales.x = {
        ...(customScales.x || {}),
        border: { color: gridColor, ...(customScales.x?.border || {}) },
        grid: { color: gridColor, ...(customScales.x?.grid || {}) },
        ticks: { color: mutedColor, ...(customScales.x?.ticks || {}) },
      }
      scales.y = {
        beginAtZero: true,
        ...(customScales.y || {}),
        border: { color: gridColor, ...(customScales.y?.border || {}) },
        grid: { color: gridColor, ...(customScales.y?.grid || {}) },
        ticks: { color: mutedColor, ...(customScales.y?.ticks || {}) },
      }
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      ...options,
      plugins,
      ...(type !== 'doughnut' || Object.keys(customScales).length ? { scales } : {}),
    }
  }, [isDarkMode, options, title, type])

  useEffect(() => {
    if (!canvasRef.current) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    chartRef.current = new Chart(canvasRef.current, {
      type,
      data,
      options: themedOptions,
    })

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
      }
    }
  }, [type, data, themedOptions])

  return <canvas ref={canvasRef} />
}
